import PropTypes from "prop-types"
import { lazy, StrictMode, Suspense } from "react"
import { Provider } from "react-redux"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { LicenseInfo } from "@mui/x-license"
import { createTheme, CssBaseline, ThemeProvider } from "@mui/material"
import InitColorSchemeScript from "@mui/material/InitColorSchemeScript"
import IntercomLauncher from "components/intercom-launcher"
import { RouterProvider } from "react-router-dom"
import useScrollbarWidth from "hooks/use-scrollbar-width"
import InitAppColorSchemeScript from "./components/theme-mode-toggle/InitAppColorSchemeScript"
import ThemePreferenceSync from "components/theme-mode-toggle/ThemePreferenceSync"
import materialTheme from "./styles/material"
import {
  DEFAULT_THEME_MODE,
  THEME_COLOR_SCHEME_STORAGE_KEY,
  THEME_MODE_STORAGE_KEY,
} from "./styles/theme-mode-config"
import "./styles/index.scss"
import "./styles/legacy.scss"
import "./styles/_theme-palette.scss"

const ReactQueryDevtools = process.env.NODE_ENV !== "production"
  ? lazy(() => import("@tanstack/react-query-devtools").then(m => ({ default: m.ReactQueryDevtools })))
  : null

LicenseInfo.setLicenseKey("3c913013b59c5f4fe39390a49a736bb3Tz0xMzAwNzEsRT0xODEyNjcxOTk5MDAwLFM9cHJlbWl1bSxMTT1hbm51YWwsUFY9aW5pdGlhbCxLVj0y")

const theme = createTheme(materialTheme)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
})

export default function Root({ store, router }) {
  useScrollbarWidth()
  return (
    <Provider store={store}>
      <>
        <InitColorSchemeScript
          defaultMode={DEFAULT_THEME_MODE}
          modeStorageKey={THEME_MODE_STORAGE_KEY}
          colorSchemeStorageKey={THEME_COLOR_SCHEME_STORAGE_KEY}
        />
        <InitAppColorSchemeScript />
        <ThemeProvider
          theme={theme}
          defaultMode={DEFAULT_THEME_MODE}
          modeStorageKey={THEME_MODE_STORAGE_KEY}
          colorSchemeStorageKey={THEME_COLOR_SCHEME_STORAGE_KEY}
          noSsr
        >
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <QueryClientProvider client={queryClient}>
              <ThemePreferenceSync />
              <StrictMode>
                <CssBaseline />
                <RouterProvider router={router} />
              </StrictMode>
              {ReactQueryDevtools && (
                <Suspense>
                  <ReactQueryDevtools initialIsOpen={false} />
                </Suspense>
              )}
            </QueryClientProvider>
          </LocalizationProvider>
        </ThemeProvider>
        <IntercomLauncher />
      </>
    </Provider>
  )
}

Root.propTypes = {
  store: PropTypes.object.isRequired,
  router: PropTypes.object.isRequired,
}
